import { Box, Flex, Icon, Portal, Text, Tooltip } from "@chakra-ui/react"
import { Link as RouterLink } from "@tanstack/react-router"
import { memo, useMemo } from "react"
import {
  FiBarChart2,
  FiBook,
  FiCalendar,
  FiClipboard,
  FiDollarSign,
  FiFile,
  FiHome,
  FiSettings,
  FiUser,
  FiUserCheck,
  FiUserPlus,
  FiUsers,
} from "react-icons/fi"
import type { IconType } from "react-icons/lib"

import { usePermissions } from "@/hooks/usePermissions"
import { Scopes } from "@/types/auth"

const baseItems = [
  { icon: FiHome, title: "Dashboard", path: "/" },
  { icon: FiSettings, title: "Settings", path: "/settings" },
]

interface SidebarItemsProps {
  onClose?: () => void
  isCollapsed?: boolean
}

interface Item {
  icon: IconType
  title: string
  path: string
}

const SidebarItems = memo(
  ({ onClose, isCollapsed = false }: SidebarItemsProps) => {
    const { hasScope, isAdmin, canReadFiles, can } = usePermissions()

    // Check if user can read management resources (staff/admin only)
    const canReadHouseholds = can("households:read")
    const canReadStudents = can("students:read")
    const canReadSessions = can("sessions:read")
    const canReadClassTemplates = can("class_templates:read")
    const canReadEnrollments = can("enrollments:read")

    // Check if user can read invoices
    const canReadInvoices = can("invoices:read") || can("invoices:read_own")

    // Parent-specific: has own-read but NOT global-read (avoids duplicates for staff/admin)
    const isParentStudents =
      hasScope("students:read_own") && !canReadStudents
    const isParentEnrollments =
      hasScope("enrollments:read_own") && !canReadEnrollments
    const isParentAttendances =
      hasScope("attendances:read_own") && !can("attendances:read")

    // Check if user is a coach (has assigned attendance permissions)
    const isCoach =
      can("attendances:read_assigned") || can("attendances:update")

    // Check if user can read reports
    const canReadReports =
      can("reports:read") ||
      can("reports:read_financial") ||
      hasScope(Scopes.SYSTEM_ADMIN)

    // Build navigation items based on user scopes
    const finalItems = useMemo(() => {
      const items: Item[] = [...baseItems]

      // Add Household Profile for parents with read_own or update_own permissions
      // This should appear early in the menu for easy access
      if (
        hasScope("households:read_own") ||
        hasScope("households:update_own")
      ) {
        items.splice(1, 0, {
          icon: FiUserCheck,
          title: "My Household",
          path: "/household",
        })
      }

      // Add management links for staff/admin users (after Dashboard)
      if (canReadHouseholds) {
        items.splice(1, 0, {
          icon: FiUsers,
          title: "Families",
          path: "/households",
        })
      }
      if (canReadStudents) {
        let idx = 1
        if (canReadHouseholds) idx++
        items.splice(idx, 0, {
          icon: FiUser,
          title: "Swimmers",
          path: "/students",
        })
      }
      if (canReadSessions) {
        let idx = 1
        if (canReadHouseholds) idx++
        if (canReadStudents) idx++
        items.splice(idx, 0, {
          icon: FiCalendar,
          title: "Seasons",
          path: "/sessions",
        })
      }
      if (canReadClassTemplates) {
        let idx = 1
        if (canReadHouseholds) idx++
        if (canReadStudents) idx++
        if (canReadSessions) idx++
        items.splice(idx, 0, {
          icon: FiBook,
          title: "Classes",
          path: "/classTemplates",
        })
      }
      if (canReadEnrollments) {
        let idx = 1
        if (canReadHouseholds) idx++
        if (canReadStudents) idx++
        if (canReadSessions) idx++
        if (canReadClassTemplates) idx++
        items.splice(idx, 0, {
          icon: FiUserPlus,
          title: "Enrollments",
          path: "/enrollments",
        })
      }

      // Parent-specific nav items (after My Household, before Invoices)
      if (isParentStudents) {
        const settingsIdx = items.findIndex((i) => i.path === "/settings")
        items.splice(settingsIdx, 0, {
          icon: FiUser,
          title: "My Swimmers",
          path: "/students",
        })
      }
      if (isParentEnrollments) {
        const settingsIdx = items.findIndex((i) => i.path === "/settings")
        items.splice(settingsIdx, 0, {
          icon: FiCalendar,
          title: "My Enrollments",
          path: "/enrollments",
        })
      }
      if (isParentAttendances) {
        const settingsIdx = items.findIndex((i) => i.path === "/settings")
        items.splice(settingsIdx, 0, {
          icon: FiClipboard,
          title: "Attendance",
          path: "/attendances",
        })
      }

      // Add Invoices navigation for users with invoice read permission
      // Insert after management links and My Household
      if (canReadInvoices) {
        // Find the right position: after all previously inserted items, before Settings
        const settingsIdx = items.findIndex((i) => i.path === "/settings")
        items.splice(settingsIdx, 0, {
          icon: FiDollarSign,
          title: "Invoices",
          path: "/invoices",
        })
      }

      // Add Files navigation only for users with files:read permission
      if (canReadFiles) {
        const settingsIdx = items.findIndex((i) => i.path === "/settings")
        items.splice(settingsIdx, 0, {
          icon: FiFile,
          title: "Files",
          path: "/files",
        })
      }

      // Add My Classes navigation for coaches
      // This provides quick access to the coach attendance dashboard
      if (isCoach) {
        const settingsIdx = items.findIndex((i) => i.path === "/settings")
        items.splice(settingsIdx, 0, {
          icon: FiClipboard,
          title: "My Classes",
          path: "/coach",
        })
      }

      // Add Reports navigation for users with reports permissions
      if (canReadReports) {
        items.push({ icon: FiBarChart2, title: "Reports", path: "/reports" })
      }

      // Add School Settings for admin users
      if (hasScope(Scopes.SYSTEM_ADMIN) || isAdmin) {
        items.push({ icon: FiSettings, title: "School Settings", path: "/schoolSettings" })
      }

      // Add Admin Dashboard only for users with admin privileges (system:admin scope or isAdmin)
      if (hasScope(Scopes.SYSTEM_ADMIN) || isAdmin) {
        items.push({ icon: FiUsers, title: "Admin Dashboard", path: "/admin" })
      }

      return items
    }, [
      canReadFiles,
      canReadHouseholds,
      canReadStudents,
      canReadSessions,
      canReadClassTemplates,
      canReadEnrollments,
      canReadInvoices,
      canReadReports,
      isCoach,
      isParentStudents,
      isParentEnrollments,
      isParentAttendances,
      hasScope,
      isAdmin,
    ])

    const listItems = useMemo(
      () =>
        finalItems.map(({ icon, title, path }) => {
          const linkContent = (
            <Flex
              gap={isCollapsed ? 0 : { base: 4, md: 2, lg: 3, xl: 4 }}
              px={isCollapsed ? 0 : { base: 4, md: 2, lg: 3, xl: 4 }}
              py={{ base: 3, md: 1.5, lg: 2 }}
              minH={{ base: "44px", md: "auto" }}
              justifyContent={isCollapsed ? "center" : "flex-start"}
              _hover={{
                background: "gray.subtle",
              }}
              _focus={{
                background: "gray.subtle",
                outline: "2px solid",
                outlineColor: "blue.500",
                outlineOffset: "2px",
              }}
              alignItems="center"
              fontSize="sm"
              role="menuitem"
              tabIndex={0}
              aria-label={`Navigate to ${title}`}
              borderRadius={isCollapsed ? "md" : undefined}
              transition="all 0.2s ease-in-out"
            >
              <Icon
                as={icon}
                alignSelf="center"
                aria-hidden="true"
                boxSize={isCollapsed ? 5 : 4}
              />
              {!isCollapsed && (
                <Text ml={{ base: 2, md: 1, lg: 2 }}>{title}</Text>
              )}
            </Flex>
          )

          if (isCollapsed) {
            return (
              <Tooltip.Root
                key={title}
                positioning={{ placement: "right" }}
                openDelay={200}
                closeDelay={100}
              >
                <Tooltip.Trigger asChild>
                  <RouterLink to={path} onClick={onClose}>
                    {linkContent}
                  </RouterLink>
                </Tooltip.Trigger>
                <Portal>
                  <Tooltip.Positioner>
                    <Tooltip.Content px={2} py={1} fontSize="sm">
                      {title}
                    </Tooltip.Content>
                  </Tooltip.Positioner>
                </Portal>
              </Tooltip.Root>
            )
          }

          return (
            <RouterLink key={title} to={path} onClick={onClose}>
              {linkContent}
            </RouterLink>
          )
        }),
      [finalItems, onClose, isCollapsed],
    )

    return (
      <Box as="nav" role="navigation" aria-label="Sidebar navigation">
        {!isCollapsed && (
          <Text
            fontSize="xs"
            px={{ base: 4, md: 2, lg: 3, xl: 4 }}
            py={{ base: 2, md: 1.5, lg: 2 }}
            fontWeight="bold"
            role="heading"
            aria-level={2}
          >
            Menu
          </Text>
        )}
        <Box as="ul" role="menu" aria-label="Navigation menu">
          {listItems}
        </Box>
      </Box>
    )
  },
)

SidebarItems.displayName = "SidebarItems"

export default SidebarItems
